/**
 * AsksTray - the questions on this screen that wait for somebody.
 *
 * THE ONE SURFACE for every ask addressed to me, in EVERY mode (v9.5.3):
 * the test the GM asks of me, a player's quiet move, a player's intent -
 * one row each (`asks/rows.jsx`), in a strip pinned under the header,
 * outside the log column, so a collapsed log or a mode switch never hides
 * them (the fuzzer found a Stealth request waiting three minutes behind
 * one). And, as before:
 *
 * - MY OWN area question (`ruling.request`): the tick list for the shot I
 *   am about to fire, or for my blast that scattered - a dialog.
 * - A question ADDRESSED TO ME (the GM): a player's proposed ticks, on the
 *   same list, to confirm or correct. Dismissing it tells the player no.
 * - What I AM WAITING ON: a line saying the GM has my proposal, with a way
 *   to withdraw it.
 *
 * A question from the wire is validated before it is drawn.
 */
import { useEffect } from 'react';
import styles from '../styles/AsksTray.module.css';
import AreaTickDialog from './AreaTickDialog';
import { readAreaAsk } from '../engine/phase/areaAsk';
import { AREA_ASK } from '../hooks/useShellAreaRuling';
import { ROWS } from './asks/rows';
import RamRuling from './asks/RamRuling';
import { RAM_ASK } from '../engine/actions/ram';

/** A player's proposal, on the GM's screen. Marked seen once it is drawn. */
function IncomingArea({ ask, asks }) {
  const { markSeen } = asks;
  useEffect(() => {
    if (ask.status === 'open') markSeen(ask.id);
  }, [ask.id, ask.status, markSeen]);
  return (
    // `display: contents`: the wrapper only names the ask this dialog answers (the fuzzer reads it).
    <div data-ask-id={ask.id} style={{ display: 'contents' }}>
      <AreaTickDialog
        key={ask.id}
        ask={readAreaAsk(ask.payload)}
        mode="confirm"
        onSubmit={(ticks) => asks.answer(ask.id, ticks)}
        onCancel={() => asks.cancel(ask.id)}
      />
    </div>
  );
}

/**
 * PINNED: TAKE COVER OR GO PRONE (2026-10-25) - one row per pinned figure this screen may answer for: the
 * player's own, the GM's prompts for NPCs and hidden figures, and (the GM) a player's, to answer for them.
 * Already prone, only the crawl is offered.
 */
function PinnedRows({ pinned }) {
  if (!pinned?.prompts.length) return null;
  return (
    <div className={styles.tray} role="status" data-testid="pinned-asks">
      {pinned.prompts.map((p) => (
        <div key={p.id} className={styles.waiting} data-testid="pinned-ask" data-ask-id={p.local ? undefined : p.id}>
          <span>
            {p.question.name} is pinned: take cover or go prone, now.{p.forOther ? ' (Answer for the player.)' : ''}
          </span>
          <button
            type="button"
            className={styles.withdraw}
            data-testid="pinned-ask-crawl"
            onClick={() => pinned.answer(p, 'crawl')}
          >
            Crawl to cover
          </button>
          {!p.question.prone && (
            <button
              type="button"
              className={styles.withdraw}
              data-testid="pinned-ask-prone"
              onClick={() => pinned.answer(p, 'prone')}
            >
              Go prone
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** What the waiting line says, by the kind of question and whether the GM has seen it. */
const WAITING = {
  [AREA_ASK]: ['Waiting for the GM to confirm who is caught.', 'The GM is looking at who is caught.'],
  [RAM_ASK]: ['Waiting for the GM to rule the ram.', 'The GM is ruling the ram.'],
};

/** My proposal is with the GM. */
function WaitingLine({ ask, onWithdraw }) {
  const [asked, seen] = WAITING[ask.kind];
  return (
    <div className={styles.waiting} role="status" data-testid="asks-waiting">
      <span>{ask.status === 'seen' ? seen : asked}</span>
      <button type="button" className={styles.withdraw} data-testid="asks-withdraw" onClick={onWithdraw}>
        Withdraw
      </button>
    </div>
  );
}

/** The asks drawn as rows: every incoming one whose kind has a row. */
function Rows({ asks, answerTest, quiet }) {
  const rows = asks.incoming.filter((a) => ROWS[a.kind]);
  if (rows.length === 0) return null;
  return (
    <div className={styles.tray} role="status" data-testid="asks-tray">
      {rows.map((ask) => {
        const Row = ROWS[ask.kind];
        return <Row key={ask.id} ask={ask} asks={asks} answerTest={answerTest} quiet={quiet} />;
      })}
    </div>
  );
}

/**
 * @param {object} props
 * @param {{request: object|null, submit: Function, cancel: Function}} props.ruling - useShellAreaRuling
 * @param {import('../hooks/useAsks').Asks} props.asks
 * @param {(ask: object) => void} props.answerTest - useAnswerTestAsk's roll
 * @param {object} [props.quiet] - useShellQuietMoves, for the GM's quiet requests
 * @param {{request: object|null, submit: Function, cancel: Function}} [props.ram] - useShellRam: my own ram to rule
 * @param {{prompts: object[], answer: Function}} [props.pinned] - useShellPinned: the Pinned questions here
 */
export default function AsksTray({ ruling, asks, answerTest, quiet, ram, pinned }) {
  const incoming = asks.incoming.find((a) => a.kind === AREA_ASK);
  const waiting = asks.outgoing.find((a) => WAITING[a.kind]);
  return (
    <>
      <Rows asks={asks} answerTest={answerTest} quiet={quiet} />
      <PinnedRows pinned={pinned} />
      {ram?.request && (
        <div className={styles.tray} role="status">
          <RamRuling question={ram.request} onSubmit={ram.submit} onCancel={ram.cancel} />
        </div>
      )}
      {ruling.request && (
        <AreaTickDialog
          ask={ruling.request.ask}
          mode={ruling.request.mode}
          onSubmit={ruling.submit}
          onCancel={ruling.cancel}
        />
      )}
      {!ruling.request && incoming && <IncomingArea ask={incoming} asks={asks} />}
      {waiting && <WaitingLine ask={waiting} onWithdraw={() => asks.cancel(waiting.id)} />}
    </>
  );
}
